import { useEffect, useState } from 'react';
import {
  PossibleCompetitorCompanyResponse,
  listPossibleCompetitorCompanies,
  peekPossibleCompetitorCompanies,
} from 'services/actions/ai_management_possible_competitors';

export interface PossibleCompetitorCompany {
  cnpj: string;
  name: string | null;
  averagePrice: number | null;
  participations: number;
  won: number;
  disqualified: number;
  samePublicBody: number;
}

export const toCompany = (item: PossibleCompetitorCompanyResponse): PossibleCompetitorCompany => ({
  cnpj: item.cnpj,
  name: item.nome,
  averagePrice: item.media_preco,
  participations: item.participacoes,
  won: item.ganhas,
  disqualified: item.desclassificadas,
  samePublicBody: item.deste_orgao,
});

export function usePossibleCompetitorCompanies(
  biddingId: number | null,
  item: string | null,
  period: number,
) {
  const peek = () =>
    biddingId && item ? peekPossibleCompetitorCompanies(biddingId, item, period) : undefined;
  const [companies, setCompanies] = useState<PossibleCompetitorCompany[]>(
    () => peek()?.data.items.map(toCompany) ?? [],
  );
  const [loading, setLoading] = useState(() => !!biddingId && !!item && !peek());
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!biddingId || !item) return undefined;

    const hit = peek();
    if (hit) {
      setCompanies(hit.data.items.map(toCompany));
      setLoading(false);
      setError(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(false);

    listPossibleCompetitorCompanies(biddingId, item, period)
      .then(({ data }) => {
        if (!active) return;
        setCompanies(data.items.map(toCompany));
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [biddingId, item, period]);

  return { companies, loading, error };
}
